import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross1Icon,
  GearIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { ArrowDownAzIcon } from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFocusWithin } from "react-aria";
import { flushSync } from "react-dom";
import {
  T,
  RecordProps,
  TLShapeUtilCanBindOpts,
  Rectangle2d,
  useValue,
  useEditor,
  Editor,
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  uniqueId,
} from "tldraw";
import { useSharedResizeObserver } from "use-shared-resize-observer";

import {
  buildBindings,
  buildManifestFromState,
  replaceCustomNodeParameters,
} from "@/components/canvas-editor/build-manifest";
import {
  BlockContainer,
  BlockContainerShapeContext,
  BlockHeader,
  BlockSeparator,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasNumberInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
  CanvasSwitch,
  CanvasTextArea,
} from "@/components/canvas-editor/components/Inputs";
import {
  BindableUtil,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  CustomNodeRootProps,
  CustomNodeRootShape,
  customNodeRootType,
  FeedShape,
  metadataType,
  paramType,
  pinnableShapes,
} from "@/components/canvas-editor/shapes/shapes";
import {
  feedId,
  preventBubble,
  previewId,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { CanvasRegexTextArea } from "@/components/CanvasRegexTextArea";
import { ListWrapper, ReadOnlyValue } from "@/components/hooks/ListContainer";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { Switch } from "@/components/ui/switch";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  FeedInputParameter,
  FeedListParameter,
  FeedNumberParameter,
  FeedSelectParameter,
  FeedToggleParameter,
  groupCustomNodeParameters,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const MIN_W = 600;
const MIN_H = 156;
const BASE_TOP_PADDING = 44;

function createMigrations() {
  const versions = createShapePropsMigrationIds(customNodeRootType, {
    RemoveFeedProps: 1,
  });

  return createShapePropsMigrationSequence({
    sequence: [
      {
        id: versions.RemoveFeedProps,
        up(props) {
          delete (props as { order?: string }).order;
          delete (props as { custom_sort_settings?: string })
            .custom_sort_settings;
        },
      },
    ],
  });
}

export class CustomNodeRootShapeUtil extends BindableUtil<CustomNodeRootShape> {
  static override migrations = createMigrations();
  static override type = customNodeRootType;

  override getType() {
    return customNodeRootType;
  }

  static override props: RecordProps<CustomNodeRootShape> = {
    h: T.number,
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    metadata: metadataType,
    customNodeParameters: T.arrayOf(paramType).optional(),
    customNodeParameterGroups: T.arrayOf(
      T.object({
        name: T.string,
        id: T.string,
        description: T.string.optional(),
      }),
    ).optional(),
    padding: T.object({
      top: T.number,
      right: T.number,
      bottom: T.number,
      left: T.number,
      gap: T.number,
    }),
    title: T.string.optional(),
    description: T.string.optional(),
    showCustomNodeParameters: T.boolean.optional(),
  };

  override getDefaultProps() {
    return {
      w: MIN_W,
      minW: MIN_W,
      h: MIN_H,
      padding: {
        top: BASE_TOP_PADDING + 40,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      title: "Feed",
      showCustomNodeParameters: true,
      metadata: undefined,
    };
  }

  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<FeedShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return (
      fromShapeType === customNodeRootType &&
      pinnableShapes.includes(toShapeType) &&
      bindingType === "layout"
    );
  }

  override canResize() {
    return true;
  }

  override component(shape: CustomNodeRootShape) {
    return (
      <CustomNodeRoot shape={shape} updateShape={this.updateProps.bind(this)} />
    );
  }

  override getGeometry(shape: CustomNodeRootShape) {
    return new Rectangle2d({
      width: Math.max(shape.props.minW || 0, shape.props.w),
      height: Math.max(MIN_H, shape.props.h),
      isFilled: true,
    });
  }

  override indicator(shape: CustomNodeRootShape) {
    return (
      <rect
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={Math.max(MIN_H, shape.props.h)}
      />
    );
  }
}

function ListParameter({
  parameter,
  shape,
  updateShape,
}: {
  parameter: FeedListParameter;
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  const inputListRef = useRef<HTMLDivElement>(null);
  const parameters = useMemo(
    () => shape.props.customNodeParameters || [],
    [shape.props.customNodeParameters],
  );
  const parameterIndex = parameters.findIndex((p) => p.name === parameter.name);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });
  const onAddAnother = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      updateShape(shape, {
        customNodeParameters: parameters.map((p, i) =>
          i === parameterIndex
            ? ({
                ...p,
                exampleValue: [...(p.exampleValue as string[]), ""],
              } as FeedListParameter)
            : p,
        ),
      });
    });

    if (!inputListRef.current) {
      return;
    }

    const inputs =
      inputListRef.current.querySelectorAll<HTMLInputElement>("input");

    inputs.values().toArray().at(-1)?.focus();
  }, [parameterIndex, shape, updateShape, parameters]);

  return (
    <div className="flex flex-col gap-2" ref={inputListRef}>
      <ListWrapper
        {...focusWithinProps}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            onAddAnother();
          }
        }}
      >
        {parameter.exampleValue.map((value, index) => (
          <div
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`param-value-${shape.id}-${index}`}
            className="relative group"
          >
            <CanvasInput
              className="flex-1"
              data-list-input={true}
              value={value}
              onValueChange={(v) => {
                updateShape(shape, {
                  customNodeParameters: parameters.map((p, i) =>
                    i === parameterIndex
                      ? ({
                          ...p,
                          exampleValue: (p.exampleValue as string[]).map(
                            (_, i2) => (i2 === index ? v : _),
                          ),
                        } as FeedListParameter)
                      : p,
                  ),
                });
              }}
            />
            <CanvasButton
              className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
              size="smIcon"
              variant="ghost"
              onClick={() => {
                updateShape(shape, {
                  customNodeParameters: parameters.map((p, i) =>
                    i === parameterIndex
                      ? ({
                          ...p,
                          exampleValue: (p.exampleValue as string[]).filter(
                            (_, i2) => i2 !== index,
                          ),
                        } as FeedListParameter)
                      : p,
                  ),
                });
              }}
            >
              <Cross1Icon />
            </CanvasButton>
          </div>
        ))}
      </ListWrapper>
      <CanvasButton size="sm" variant="ghost" onPress={onAddAnother}>
        Add Another
        {isFocusWithin && (
          <KbdGroup className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2">
            <Kbd>Meta + ⏎</Kbd>
          </KbdGroup>
        )}
      </CanvasButton>
    </div>
  );
}

function SelectParameter({
  parameter,
  shape,
  updateShape,
  parameterIndex,
}: {
  parameter: FeedSelectParameter;
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
  parameterIndex: number;
}) {
  const parameters = shape.props.customNodeParameters || [];
  const inputListRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`param-example-${parameterIndex}`} className="p-1">
          Custom Labels
        </Label>
        <div className="flex flex-col gap-2">
          <CanvasSwitch
            checked={Boolean(parameter.labels)}
            onCheckedChange={(v) => {
              updateShape(shape, {
                customNodeParameters: parameters.map((p, i) =>
                  i === parameterIndex
                    ? ({
                        ...p,
                        labels: v
                          ? (p as FeedSelectParameter).options.map(() => "")
                          : undefined,
                      } as FeedSelectParameter)
                    : p,
                ),
              });
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`param-example-${parameterIndex}`} className="p-1">
          Options
        </Label>
        <div className="flex flex-col gap-2" ref={inputListRef}>
          <div className="flex flex-col gap-1">
            {parameter.labels && (
              <div className="flex items-center gap-1 text-xs text-slate-dim">
                <div className="flex-1 px-1">Label</div>
                <div className="flex-1 px-1">Value</div>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {parameter.options.map((value, index) => (
                <div
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`param-value-${shape.id}-${index}`}
                  className="relative group flex items-center gap-1"
                >
                  {typeof parameter.labels?.[index] !== "undefined" && (
                    <CanvasInput
                      className="flex-1"
                      data-list-input={true}
                      value={parameter.labels?.[index]}
                      onValueChange={(v) => {
                        updateShape(shape, {
                          customNodeParameters: parameters.map((p, i) =>
                            i === parameterIndex
                              ? ({
                                  ...p,
                                  labels: (
                                    p as FeedSelectParameter
                                  ).labels?.map((_, i2) =>
                                    i2 === index ? v : _,
                                  ),
                                } as FeedSelectParameter)
                              : p,
                          ),
                        });
                      }}
                    />
                  )}
                  <CanvasInput
                    className="flex-1"
                    data-list-input={true}
                    value={value}
                    onValueChange={(v) => {
                      updateShape(shape, {
                        customNodeParameters: parameters.map((p, i) =>
                          i === parameterIndex
                            ? ({
                                ...p,
                                options: (p as FeedSelectParameter).options.map(
                                  (_, i2) => (i2 === index ? v : _),
                                ),
                              } as FeedSelectParameter)
                            : p,
                        ),
                      });
                    }}
                  />
                  <CanvasButton
                    className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                    size="smIcon"
                    variant="ghost"
                    onClick={() => {
                      updateShape(shape, {
                        customNodeParameters: parameters.map((p, i) =>
                          i === parameterIndex
                            ? ({
                                ...p,
                                options: (
                                  p as FeedSelectParameter
                                ).options.filter((_, i2) => i2 !== index),
                              } as FeedSelectParameter)
                            : p,
                        ),
                      });
                    }}
                  >
                    <Cross1Icon />
                  </CanvasButton>
                </div>
              ))}
            </div>
          </div>
          <CanvasButton
            size="sm"
            variant="ghost"
            onPress={() => {
              // eslint-disable-next-line @eslint-react/dom/no-flush-sync
              flushSync(() => {
                updateShape(shape, {
                  customNodeParameters: parameters.map((p, i) => {
                    if (i === parameterIndex) {
                      const newOption = {
                        ...p,
                      } as FeedSelectParameter;

                      newOption.options = [...newOption.options, ""];

                      if (newOption.labels) {
                        newOption.labels = [...newOption.labels, ""];
                      }

                      return newOption;
                    }

                    return p;
                  }),
                });
              });

              if (!inputListRef.current) {
                return;
              }

              const inputs =
                inputListRef.current.querySelectorAll<HTMLInputElement>(
                  "[data-list-input]",
                );

              inputs.values().toArray().at(-1)?.focus();
            }}
          >
            Add Option
          </CanvasButton>
        </div>
      </div>
    </>
  );
}

function CustomNodeParameters({
  shape,
  updateShape,
}: {
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  const parameters = shape.props.customNodeParameters || [];
  const isReadOnly = useIsReadonlyEditor();

  if (isReadOnly) {
    const groups = groupCustomNodeParameters(parameters);

    return (
      <div className="flex flex-col gap-4 p-3 pt-4" {...preventBubble}>
        {Object.entries(groups).map(([groupId, groupParameters]) => {
          const group = shape.props.customNodeParameterGroups?.find(
            (g) => g.id === groupId,
          );

          return (
            <div key={groupId} className="flex flex-col gap-2">
              {group?.name && (
                <h3 className="text-xl font-bold">{group.name}</h3>
              )}
              {group?.description && (
                <div className="text-sm text-slate-dim">
                  {group.description}
                </div>
              )}
              {groupParameters.length > 0 && (
                <div className="flex flex-col gap-2">
                  {groupParameters.map((parameter, parameterIndex) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <Fragment key={`param-${shape.id}-${parameterIndex}`}>
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <div className="text-lg font-medium flex-1 flex items-center gap-3">
                              <span className="flex-1">
                                {parameter.displayName || `$${parameter.name}`}
                              </span>
                              <ReadOnlyValue>
                                {parameter.type || "input"}
                              </ReadOnlyValue>
                            </div>
                          </div>

                          {parameter.description && (
                            <div className="flex flex-col gap-1 text-slate-dim">
                              {parameter.description}
                            </div>
                          )}

                          {parameter.type === "select" && (
                            <div className="flex flex-col gap-1">
                              <div className="py-1 text-sm">Options:</div>
                              <div className="flex flex-wrap gap-2">
                                {parameter.options.map((option, index) => (
                                  <ReadOnlyValue
                                    // eslint-disable-next-line @eslint-react/no-array-index-key
                                    key={`param-option-${shape.id}-${index}`}
                                  >
                                    {parameter.labels
                                      ? parameter.labels[index]
                                      : option}
                                  </ReadOnlyValue>
                                ))}
                              </div>
                            </div>
                          )}

                          {parameter.exampleValue && (
                            <div className="flex flex-col gap-1">
                              <div className="py-1 text-sm">Default Value:</div>

                              {parameter.type === "input" ||
                              parameter.type === "regex" ||
                              !parameter.type ? (
                                <ReadOnlyValue>
                                  {parameter.exampleValue}
                                </ReadOnlyValue>
                              ) : parameter.type === "select" ? (
                                <ReadOnlyValue>
                                  {parameter.labels
                                    ? parameter.labels[parameterIndex]
                                    : parameter.exampleValue}
                                </ReadOnlyValue>
                              ) : parameter.type === "toggle" ? (
                                <Switch checked={parameter.exampleValue} />
                              ) : parameter.type === "number" ? (
                                <ReadOnlyValue>
                                  {parameter.exampleValue}
                                </ReadOnlyValue>
                              ) : parameter.type === "list" ? (
                                <div className="flex flex-wrap gap-2">
                                  {parameter.exampleValue.map(
                                    (value, index) => (
                                      <ReadOnlyValue
                                        // eslint-disable-next-line @eslint-react/no-array-index-key
                                        key={`param-value-${shape.id}-${index}`}
                                      >
                                        {value}
                                      </ReadOnlyValue>
                                    ),
                                  )}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>

                      {parameterIndex !== groupParameters.length - 1 && (
                        <BlockSeparator className="my-2" />
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-3 pt-4" {...preventBubble}>
      {parameters.length > 0 && (
        <div className="flex flex-col gap-2">
          {parameters.map((parameter, parameterIndex) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Fragment key={`param-${shape.id}-${parameterIndex}`}>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <div className="flex flex-col gap-1 flex-1">
                      <Label
                        htmlFor={`param-name-${parameterIndex}`}
                        className="p-1"
                      >
                        Name
                      </Label>
                      <CanvasInput
                        id={`param-name-${parameterIndex}`}
                        className="flex-1"
                        value={parameter.name}
                        required
                        onInput={(e) => {
                          if (e.currentTarget.value.match(/^\$/)) {
                            e.currentTarget.setCustomValidity(
                              "Custom node parameters cannot start with a $. You can use $ to reference a parameter in your node's logic.",
                            );
                            e.currentTarget.reportValidity();
                          } else if (
                            !e.currentTarget.value.match(/^[a-zA-Z0-9_]+$/)
                          ) {
                            e.currentTarget.setCustomValidity(
                              "Custom node parameters can only contain letters, numbers, and underscores.",
                            );
                            e.currentTarget.reportValidity();
                          } else {
                            e.currentTarget.setCustomValidity("");
                            e.currentTarget.reportValidity();
                          }
                        }}
                        onValueChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) =>
                              i === parameterIndex ? { ...p, name: v } : p,
                            ),
                          });
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-1">
                      <Label
                        htmlFor={`param-display-name-${parameterIndex}`}
                        className="p-1"
                      >
                        Display Name
                      </Label>
                      <CanvasInput
                        id={`param-display-name-${parameterIndex}`}
                        className="flex-1"
                        required={false}
                        value={parameter.displayName || ""}
                        onValueChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) =>
                              i === parameterIndex
                                ? { ...p, displayName: v }
                                : p,
                            ),
                          });
                        }}
                      />
                    </div>
                    {shape.props.customNodeParameterGroups &&
                      shape.props.customNodeParameterGroups.length > 0 && (
                        <div className="flex flex-col gap-1 flex-1">
                          <Label
                            htmlFor={`param-group-${parameterIndex}`}
                            className="p-1"
                          >
                            Group
                          </Label>
                          <CanvasSimpleSelect
                            id={`param-group-${parameterIndex}`}
                            className="flex-1"
                            required={false}
                            options={[
                              {
                                value: "none",
                                label: "None",
                              },
                              ...shape.props.customNodeParameterGroups.map(
                                (g) => ({
                                  value: g.id,
                                  label: g.name || "Unnamed",
                                }),
                              ),
                            ]}
                            value={parameter.group || "none"}
                            aria-label="Group"
                            onValueChange={(v) => {
                              updateShape(shape, {
                                customNodeParameters: parameters.map((p, i) =>
                                  i === parameterIndex ? { ...p, group: v } : p,
                                ),
                              });
                            }}
                          />
                        </div>
                      )}
                    <div className="flex flex-col gap-1 w-[120px]">
                      <Label
                        htmlFor={`param-example-${parameterIndex}`}
                        className="p-1"
                      >
                        Type
                      </Label>
                      <CanvasSimpleSelect
                        aria-label="Type"
                        className="flex-1"
                        value={parameter.type || "input"}
                        options={[
                          "input",
                          "regex",
                          "list",
                          "toggle",
                          "number",
                          "select",
                        ]}
                        onValueChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) => {
                              if (i === parameterIndex) {
                                if (v === "input") {
                                  return {
                                    description: p.description,
                                    name: p.name,
                                    type: "input",
                                    exampleValue: Array.isArray(p.exampleValue)
                                      ? p.exampleValue[0]
                                      : `${p.exampleValue}`,
                                  } as FeedInputParameter;
                                } else if (v === "regex") {
                                  return {
                                    description: p.description,
                                    name: p.name,
                                    type: "regex",
                                    exampleValue: Array.isArray(p.exampleValue)
                                      ? p.exampleValue[0]
                                      : `${p.exampleValue}`,
                                  } as FeedInputParameter;
                                } else if (v === "toggle") {
                                  return {
                                    description: p.description,
                                    name: p.name,
                                    type: "toggle",
                                    exampleValue: false,
                                  } as FeedToggleParameter;
                                } else if (v === "number") {
                                  return {
                                    description: p.description,
                                    name: p.name,
                                    type: "number",
                                    exampleValue: 0,
                                  } as FeedNumberParameter;
                                } else if (v === "select") {
                                  return {
                                    description: p.description,
                                    name: p.name,
                                    type: "select",
                                    options: [""],
                                    exampleValue: "",
                                  } as FeedSelectParameter;
                                }

                                return {
                                  ...p,
                                  type: "list",
                                  exampleValue:
                                    typeof p.exampleValue === "string"
                                      ? [p.exampleValue]
                                      : [],
                                } as FeedListParameter;
                              } else {
                                return p;
                              }
                            }),
                          });
                        }}
                      />
                    </div>
                    <div className="self-end flex items-center gap-0.5">
                      <DropdownMenu>
                        <MenuTrigger asChild>
                          <CanvasButton variant="canvas" size="icon">
                            <span className="sr-only">Settings</span>
                            <GearIcon />
                          </CanvasButton>
                        </MenuTrigger>
                        <MenuContent align="end">
                          {parameter.type === "list" && (
                            <>
                              <MenuItem
                                onSelect={() => {
                                  const sorted = [
                                    ...parameter.exampleValue,
                                  ].sort();

                                  if (
                                    JSON.stringify(sorted) ===
                                    JSON.stringify(parameter.exampleValue)
                                  ) {
                                    sorted.reverse();
                                  }

                                  updateShape(shape, {
                                    customNodeParameters: parameters.map(
                                      (p, i) =>
                                        i === parameterIndex
                                          ? ({
                                              ...p,
                                              exampleValue: sorted,
                                            } as FeedListParameter)
                                          : p,
                                    ),
                                  });
                                }}
                              >
                                <ArrowDownAzIcon />
                                Sort Alphabetically
                              </MenuItem>
                              <MenuSeparator />
                            </>
                          )}

                          <MenuItem
                            disabled={parameterIndex === 0}
                            onClick={() => {
                              const newParameters = [...parameters];
                              const temp = newParameters[parameterIndex];
                              const prev = newParameters[parameterIndex - 1];

                              if (!prev || !temp) {
                                return;
                              }

                              newParameters[parameterIndex] = prev;
                              newParameters[parameterIndex - 1] = temp;

                              updateShape(shape, {
                                customNodeParameters: newParameters,
                              });
                            }}
                          >
                            <ChevronUpIcon />
                            Move Up
                          </MenuItem>
                          <MenuItem
                            disabled={parameterIndex === parameters.length - 1}
                            onSelect={() => {
                              const newParameters = [...parameters];
                              const temp = newParameters[parameterIndex];
                              const next = newParameters[parameterIndex + 1];

                              if (!next || !temp) {
                                return;
                              }

                              newParameters[parameterIndex] = next;
                              newParameters[parameterIndex + 1] = temp;

                              updateShape(shape, {
                                customNodeParameters: newParameters,
                              });
                            }}
                          >
                            <ChevronDownIcon />
                            Move Down
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem
                            onSelect={() => {
                              updateShape(shape, {
                                customNodeParameters: parameters.filter(
                                  (_, i) => i !== parameterIndex,
                                ),
                              });
                            }}
                          >
                            <TrashIcon />
                            Remove
                          </MenuItem>
                        </MenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor={`param-description-${parameterIndex}`}
                      className="p-1"
                    >
                      Description
                    </Label>
                    <CanvasTextArea
                      className="flex-1"
                      value={parameter.description}
                      required={false}
                      onValueChange={(v) => {
                        updateShape(shape, {
                          customNodeParameters: parameters.map((p, i) =>
                            i === parameterIndex ? { ...p, description: v } : p,
                          ),
                        });
                      }}
                    />
                  </div>

                  {parameter.type === "select" && (
                    <SelectParameter
                      parameterIndex={parameterIndex}
                      parameter={parameter}
                      shape={shape}
                      updateShape={updateShape}
                    />
                  )}

                  {parameter.type === "number" && (
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={`param-example-${parameterIndex}`}
                        className="p-1"
                      >
                        Is Percentage?
                      </Label>
                      <div className="flex flex-col gap-2">
                        <CanvasSwitch
                          checked={parameter.isPercentage ?? false}
                          onCheckedChange={(v) => {
                            updateShape(shape, {
                              customNodeParameters: parameters.map((p, i) =>
                                i === parameterIndex
                                  ? ({
                                      ...p,
                                      isPercentage: v,
                                    } as FeedNumberParameter)
                                  : p,
                              ),
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor={`param-example-${parameterIndex}`}
                      className="p-1"
                    >
                      Default Value
                    </Label>

                    {parameter.type === "input" || !parameter.type ? (
                      <CanvasInput
                        className="flex-1"
                        value={parameter.exampleValue}
                        required={false}
                        onValueChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) =>
                              i === parameterIndex
                                ? ({
                                    ...p,
                                    exampleValue: v,
                                  } as FeedInputParameter)
                                : p,
                            ),
                          });
                        }}
                      />
                    ) : parameter.type === "regex" ? (
                      <CanvasRegexTextArea
                        isReadOnly={false}
                        value={parameter.exampleValue}
                        onChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) =>
                              i === parameterIndex
                                ? ({
                                    ...p,
                                    exampleValue: v,
                                  } as FeedInputParameter)
                                : p,
                            ),
                          });
                        }}
                      />
                    ) : parameter.type === "toggle" ? (
                      <div className="p-2">
                        <CanvasSwitch
                          checked={parameter.exampleValue}
                          onCheckedChange={(v) => {
                            updateShape(shape, {
                              customNodeParameters: parameters.map((p, i) =>
                                i === parameterIndex
                                  ? ({
                                      ...p,
                                      exampleValue: v,
                                    } as FeedToggleParameter)
                                  : p,
                              ),
                            });
                          }}
                        />
                      </div>
                    ) : parameter.type === "number" ? (
                      parameter.isPercentage ? (
                        <CanvasPercentageInput
                          isCustomNodeParameter={true}
                          value={parameter.exampleValue}
                          onValueChange={(v) => {
                            updateShape(shape, {
                              customNodeParameters: parameters.map((p, i) =>
                                i === parameterIndex
                                  ? ({
                                      ...p,
                                      exampleValue: v,
                                    } as FeedNumberParameter)
                                  : p,
                              ),
                            });
                          }}
                        />
                      ) : (
                        <CanvasNumberInput
                          isCustomNodeParameter={true}
                          value={parameter.exampleValue}
                          onValueChange={(v) => {
                            updateShape(shape, {
                              customNodeParameters: parameters.map((p, i) =>
                                i === parameterIndex
                                  ? ({
                                      ...p,
                                      exampleValue: v,
                                    } as FeedNumberParameter)
                                  : p,
                              ),
                            });
                          }}
                        />
                      )
                    ) : parameter.type === "list" ? (
                      <ListParameter
                        parameter={parameter}
                        shape={shape}
                        updateShape={updateShape}
                      />
                    ) : parameter.type === "select" ? (
                      <CanvasSimpleSelect
                        aria-label="Select parameter default value"
                        options={parameter.options.map((o, index) => ({
                          label: parameter.labels?.[index] ?? o,
                          value: o === "" ? "empty" : o,
                        }))}
                        value={parameter.exampleValue}
                        onValueChange={(v) => {
                          updateShape(shape, {
                            customNodeParameters: parameters.map((p, i) =>
                              i === parameterIndex
                                ? ({
                                    ...p,
                                    exampleValue: v,
                                  } as FeedSelectParameter)
                                : p,
                            ),
                          });
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <BlockSeparator className="my-4" />
            </Fragment>
          ))}
        </div>
      )}
      <CanvasButton
        variant="ghost"
        size="sm"
        onClick={() => {
          updateShape(shape, {
            customNodeParameters: [
              ...parameters,
              {
                name: "",
                description: "",
                exampleValue: "",
              },
            ],
          });
        }}
      >
        Add Parameter
      </CanvasButton>
    </div>
  );
}

function NoParamPaddingUpdate({
  shape,
  updateShape,
}: {
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  useEffect(() => {
    updateShape(shape, {
      padding: {
        ...shape.props.padding,
        top: 100,
      },
    });
  }, [shape, updateShape]);
  return null;
}

function CustomNodeParametersSection({
  shape,
  updateShape,
}: {
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  const isReadonly = useIsReadonlyEditor();

  if (
    isReadonly &&
    (!shape.props.customNodeParameters ||
      shape.props.customNodeParameters.length === 0)
  ) {
    return <NoParamPaddingUpdate shape={shape} updateShape={updateShape} />;
  }

  return (
    <BlockContainer
      asDiv={true}
      variant="amber"
      className="px-3.5 pt-4 flex flex-col gap-2 group pointer-events-auto sort-area-block"
      dynamicHeight={true}
    >
      <BlockHeader
        description={
          <div className="flex flex-col gap-1">
            <p>
              Parameters allow users of your node to provide values to customize
              the functionality of your node.
            </p>
            <p>To use a parameter use $parameterName in your node's logic.</p>
          </div>
        }
      >
        Parameters
      </BlockHeader>
      <CustomNodeParameters shape={shape} updateShape={updateShape} />
    </BlockContainer>
  );
}

function CustomNodeParameterGroupSection({
  shape,
  updateShape,
}: {
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  return (
    <BlockContainer
      asDiv={true}
      variant="amber"
      className="px-3.5 pt-4 flex flex-col gap-2 group pointer-events-auto sort-area-block"
      dynamicHeight={true}
    >
      <BlockHeader
        description={
          <div className="flex flex-col gap-1">
            <p>
              Parameters allow users of your node to provide values to customize
              the functionality of your node.
            </p>
            <p>To use a parameter use $parameterName in your node's logic.</p>
          </div>
        }
      >
        Parameter Groups
      </BlockHeader>

      <div className="flex flex-col gap-4 p-3 pt-4" {...preventBubble}>
        {shape.props.customNodeParameterGroups?.map((group, index) => (
          <div key={group.id} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <Label htmlFor={`group-name-${group.id}`}>Name</Label>
                <CanvasInput
                  id={`group-name-${group.id}`}
                  value={group.name}
                  onValueChange={(v) => {
                    updateShape(shape, {
                      customNodeParameterGroups:
                        shape.props.customNodeParameterGroups?.map((g) =>
                          g.id === group.id ? { ...g, name: v } : g,
                        ),
                    });
                  }}
                />
              </div>
              <SimpleTooltip message="Delete Group" className="self-end">
                <CanvasButton
                  variant="canvas"
                  size="icon"
                  onClick={() => {
                    updateShape(shape, {
                      customNodeParameters:
                        shape.props.customNodeParameters?.map((p) =>
                          p.group === group.id ? { ...p, group: undefined } : p,
                        ),
                      customNodeParameterGroups:
                        shape.props.customNodeParameterGroups?.filter(
                          (g) => g.id !== group.id,
                        ),
                    });
                  }}
                >
                  <span className="sr-only">Delete Group</span>
                  <TrashIcon className="w-4 h-4" />
                </CanvasButton>
              </SimpleTooltip>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`group-description-${group.id}`}>
                Description
              </Label>
              <CanvasTextArea
                id={`group-description-${group.id}`}
                value={group.description}
                onValueChange={(v) => {
                  updateShape(shape, {
                    customNodeParameterGroups:
                      shape.props.customNodeParameterGroups?.map((g) =>
                        g.id === group.id ? { ...g, description: v } : g,
                      ),
                  });
                }}
              />
            </div>
            {shape.props.customNodeParameterGroups &&
              index !== shape.props.customNodeParameterGroups.length - 1 && (
                <BlockSeparator className="my-4" />
              )}
          </div>
        ))}
        <CanvasButton
          variant="ghost"
          size="sm"
          onClick={() => {
            updateShape(shape, {
              customNodeParameterGroups: [
                ...(shape.props.customNodeParameterGroups || []),
                {
                  name: "",
                  id: uniqueId(),
                },
              ],
            });
          }}
        >
          Add Group
        </CanvasButton>
      </div>
    </BlockContainer>
  );
}

function ConfigurationHeightUpdater({
  updateShape,
  shape,
  children,
}: {
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
  shape: CustomNodeRootShape;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useSharedResizeObserver({
    ref,
    onUpdate: ({ contentRect }) => {
      const newPadding = BASE_TOP_PADDING + contentRect.height - 8;
      const paddingDelta = newPadding - shape.props.padding.top;

      updateShape(shape, {
        h: shape.props.h + paddingDelta,
        padding: {
          ...shape.props.padding,
          top: newPadding,
        },
      });
    },
  });

  return (
    <div ref={ref} className="flex flex-col">
      {children}
    </div>
  );
}

function CustomNodeRoot({
  shape,
  updateShape,
}: {
  shape: CustomNodeRootShape;
  updateShape: (
    shape: CustomNodeRootShape,
    props: Partial<CustomNodeRootProps>,
  ) => void;
}) {
  const isReadonly = useIsReadonlyEditor();
  const editor = useEditor();
  const id = useValue(
    "logic-atom",
    () => {
      return dropTargetId.get();
    },
    [dropTargetId],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={Math.max(MIN_H, shape.props.h)}
        className={cn(
          "flex flex-col gap-2",
          shape.id === id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        onUpdateDimensions={({ h }) => {
          if (shape.props.showCustomNodeParameters) {
            return;
          }
          updateShape(shape, {
            h,
            padding: {
              ...shape.props.padding,
              top: 44,
            },
          });
        }}
      >
        <BlockHeader
          description={
            shape.props.description || (
              <div className="flex flex-col gap-1">
                <div>The root custom node definition.</div>
                <div className="text-xs text-slate-dim">
                  The custom node can have 1 child node. To build more complex
                  custom nodes, use the logic node.
                </div>
              </div>
            )
          }
        >
          {shape.props.title || "Custom Node"}
        </BlockHeader>

        {!shape.props.showCustomNodeParameters ? null : (
          <ConfigurationHeightUpdater shape={shape} updateShape={updateShape}>
            <CustomNodeParametersSection
              shape={shape}
              updateShape={updateShape}
            />
            {!isReadonly && (
              <CustomNodeParameterGroupSection
                shape={shape}
                updateShape={updateShape}
              />
            )}
            <CanvasButton
              alwaysEnabled
              className="m-3"
              variant="canvas"
              onClick={() => {
                const manifest = buildManifestFromState(editor);

                if (!manifest) return;

                editor.deleteShape(previewId);

                const { id } = buildBindings({
                  editor,
                  id: previewId,
                  isReadOnly: true,
                  filter: replaceCustomNodeParameters(
                    manifest.manifest.filter,
                    shape.props.customNodeParameters || [],
                  ),
                });

                const newShape = editor.getShape(id);

                if (!newShape) return;

                const customNodeShape =
                  editor.getShape<CustomNodeRootShape>(feedId);

                if (!customNodeShape) return;

                editor.updateShape({
                  ...newShape,
                  x: customNodeShape.x + customNodeShape.props.w + 32,
                  y:
                    customNodeShape.y +
                    customNodeShape.props.padding.top +
                    customNodeShape.props.padding.gap -
                    15,
                  props: {
                    ...newShape.props,
                    w: customNodeShape.props.w,
                  },
                });
              }}
            >
              Preview
            </CanvasButton>
          </ConfigurationHeightUpdater>
        )}
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}

export function getCustomNodeRootShape(editor: Editor) {
  return editor
    .getCurrentPageShapes()
    .find((s) => s.type === customNodeRootType) as
    | CustomNodeRootShape
    | undefined;
}
